import { Box, Stack } from '@mui/material';
import { GridRenderCellParams } from '@mui/x-data-grid-pro';
import { compact, startsWith } from 'lodash';
import { useState } from 'react';
import { useWatch } from 'react-hook-form';

import { IconButton } from '@/components/form/baseInputs/Button/IconButton';
import { FormAwareCheckbox } from '@/components/form/formAwareInputs/FormAwareCheckbox';
import { FormAwarePercentInput } from '@/components/form/formAwareInputs/FormAwarePercentInput';
import { FormAwareRadioGroup } from '@/components/form/formAwareInputs/FormAwareRadioGroup';
import { FormAwareSelectInput } from '@/components/form/formAwareInputs/FormAwareSelectInput';
import { FormAwareTextInput } from '@/components/form/formAwareInputs/FormAwareTextInput';
import { FormAwareTypeaheadMultiSelectInput } from '@/components/form/formAwareInputs/FormAwareTypeaheadMultiSelectInput';
import { CheckIcon } from '@/components/icons/CheckIcon';
import { Edit02Icon } from '@/components/icons/Edit02Icon';
import { Trash01Icon } from '@/components/icons/Trash01Icon';
import { PopperContent } from '@/components/poppers/PopperContent';
import { ProUpsellTooltip } from '@/components/poppers/ProUpsellTooltip';
import { ProUpsellIcon } from '@/components/poppers/ProUpsellTooltip/ProUpsellIcon';
import { useFormContext } from '@/components/react-hook-form';
import {
  CellContainer,
  PrimaryCellTypography,
} from '@/components/tables/DataTable/components/cells';
import { SHOW_ON_ROW_HOVER_CLASSNAME } from '@/components/tables/DataTable/components/ThemedDataGrid';
import { Column } from '@/components/tables/DataTable/types';
import { useBalanceSheetCategoriesForDropdown } from '@/modules/assetCategories/hooks/useBalanceSheetCategoriesForDropdown';
import { useTenantHasEnabledAssetIntegration } from '@/modules/assetProviderIntegrations/shared/hooks/useTenantHasEnabledAssetIntegration';
import { useFeatureFlag } from '@/modules/featureFlags/useFeatureFlag';
import { useTenantDetailsContext } from '@/modules/tenant/TenantDetailsContext/TenantDetailsContext';
import { AssetClassLiquidityStatus } from '@/types/schema';

import {
  AssetCategoriesForm,
  AssetCategoriesFormPaths,
  NEW_ASSET_CATEGORY_SENTINEL,
  RowData,
} from './AdminAssetCategoriesConfigurationPage.types';
import { AssetCategoryDeletionModal } from './components/AssetCategoryDeletionModal/AssetCategoryDeletionModal';
import { SystemClassIndicator } from './components/SystemClassIndicator';
import { useReorderableRows } from './context/ReorderableRows.context';
import { useAvailableIntegrationCategories } from './hooks/useAvailableIntegrationCategories';

export function useAssetCategoriesColumns() {
  const integrationEnabled = useTenantHasEnabledAssetIntegration();
  const trustIncomeTaxEnabled = useFeatureFlag('trust_income_tax');
  const columns: Column<RowData>[] = compact([
    {
      field: 'name',
      headerName: 'Asset category',
      flex: 1,
      minWidth: 250,
      sortable: false,
      renderCell: AssetCategoryNameInputRenderer,
    },
    {
      field: 'balanceSheetCategory',
      headerName: 'Balance sheet category',
      width: 320,
      sortable: false,
      renderCell: BalanceSheetCategoryInputRenderer,
    },
    integrationEnabled && {
      field: 'integrationCategories',
      headerName: 'Associated integration categories',
      width: 420,
      sortable: false,
      renderCell: AssociatedIntegrationAssetClassesInputRenderer,
    },
    {
      field: 'growthRate',
      headerName: 'Growth rate',
      width: 150,
      sortable: false,
      renderCell: GrowthRateInputRenderer,
    },
    trustIncomeTaxEnabled && {
      field: 'taxDragRate',
      headerName: 'Tax drag rate',
      contextualHelp: (
        <PopperContent body="Tax drag is the portion of this asset's annual return assumed to be paid out in taxes, reducing the net growth rate used in the Estate Waterfall. Enter the percentage of return lost to taxes each year. Example: a $1,000,000 asset growing 10% earns $100,000 pre-tax. A 20% tax drag assumes $20,000 goes to taxes, so the asset grows at a net 8%" />
      ),
      width: 175,
      sortable: false,
      renderCell: TaxDragRateInputRenderer,
    },
    {
      field: 'liquidity',
      headerName: 'Liquid / Illiquid',
      width: 120,
      sortable: false,
      renderCell: LiquityStatusInputRenderer,
    },
    {
      field: 'isRealTpp',
      headerName: 'Real and tangible personal property',
      width: 240,
      sortable: false,
      renderCell: RealTppInputRenderer,
    },
  ]);

  return columns;
}

function RealTppInputRenderer({ row, colDef }: GridRenderCellParams<RowData>) {
  const { control } = useFormContext<AssetCategoriesForm>();
  const assetClassId = row.id;

  return (
    <CellContainer align={colDef.align}>
      <FormAwareCheckbox
        fieldName={
          `assetCategoriesById.${assetClassId}.isRealTpp` as const satisfies AssetCategoriesFormPaths
        }
        label="Real and tangible personal property"
        hideLabel
        control={control}
        disabled={row.isSystemClass}
      />
    </CellContainer>
  );
}

function BalanceSheetCategoryInputRenderer({
  row,
  colDef,
}: GridRenderCellParams<RowData>) {
  const { control } = useFormContext<AssetCategoriesForm>();
  // For the Liability/Receivable asset classes, include their respective balance sheet categories
  const { options, loading } = useBalanceSheetCategoriesForDropdown({
    includeLiabilities: row.isLiabilityClass,
    includeReceivables: row.isReceivableClass,
  });
  const assetClassId = row.id;
  return (
    <CellContainer align={colDef.align}>
      <Box width="100%">
        <FormAwareSelectInput
          fieldName={
            `assetCategoriesById.${assetClassId}.balanceSheetCategoryID` as const satisfies AssetCategoriesFormPaths
          }
          label="Balance sheet category"
          hideLabel
          control={control}
          options={options}
          loading={loading}
          disabled={row.isSystemClass}
        />
      </Box>
    </CellContainer>
  );
}

function AssociatedIntegrationAssetClassesInputRenderer({
  row,
  colDef,
}: GridRenderCellParams<RowData>) {
  const { options, loading } = useAvailableIntegrationCategories({
    currentRowId: row.id,
  });
  const [inputValue, setInputValue] = useState('');
  const { control } = useFormContext<AssetCategoriesForm>();
  const assetClassId = row.id;
  return (
    <CellContainer align={colDef.align}>
      <Box width="100%">
        <FormAwareTypeaheadMultiSelectInput
          fieldName={
            `assetCategoriesById.${assetClassId}.integrationCategoryIds` as const satisfies AssetCategoriesFormPaths
          }
          label="Associated asset classes"
          hideLabel
          loading={loading}
          control={control}
          inputValue={inputValue}
          onInputChange={setInputValue}
          options={options}
        />
      </Box>
    </CellContainer>
  );
}

function GrowthRateInputRenderer({
  row,
  colDef,
}: GridRenderCellParams<RowData>) {
  const { projectionFunctionalityEnabled } = useTenantDetailsContext();
  const { control } = useFormContext<AssetCategoriesForm>();
  const assetClassId = row.id;

  // Hide growth rate input for the Liability and Receivable asset classes
  if (row.isLiabilityClass || row.isReceivableClass) {
    return <CellContainer align={colDef.align} />;
  }

  return (
    <CellContainer align={colDef.align}>
      <Box width="100%">
        <ProUpsellTooltip show={!projectionFunctionalityEnabled}>
          <FormAwarePercentInput
            isDecimalJSInput
            fieldName={
              `assetCategoriesById.${assetClassId}.growthRate` as const satisfies AssetCategoriesFormPaths
            }
            label="Growth rate"
            hideLabel
            control={control}
            disabled={!projectionFunctionalityEnabled}
            endAdornment={!projectionFunctionalityEnabled && <ProUpsellIcon />}
          />
        </ProUpsellTooltip>
      </Box>
    </CellContainer>
  );
}

function TaxDragRateInputRenderer({
  row,
  colDef,
}: GridRenderCellParams<RowData>) {
  const { projectionFunctionalityEnabled } = useTenantDetailsContext();
  const { control } = useFormContext<AssetCategoriesForm>();
  const assetClassId = row.id;

  // Hide tax drag rate input for the Liability and Receivable asset classes
  if (row.isLiabilityClass || row.isReceivableClass) {
    return <CellContainer align={colDef.align} />;
  }

  return (
    <CellContainer align={colDef.align}>
      <Box width="100%">
        <ProUpsellTooltip show={!projectionFunctionalityEnabled}>
          <FormAwarePercentInput
            isDecimalJSInput
            fieldName={
              `assetCategoriesById.${assetClassId}.taxDragRate` as const satisfies AssetCategoriesFormPaths
            }
            label="Tax drag rate"
            hideLabel
            control={control}
            disabled={!projectionFunctionalityEnabled}
            endAdornment={!projectionFunctionalityEnabled && <ProUpsellIcon />}
          />
        </ProUpsellTooltip>
      </Box>
    </CellContainer>
  );
}

function LiquityStatusInputRenderer({
  row,
  colDef,
}: GridRenderCellParams<RowData>) {
  const { control } = useFormContext<AssetCategoriesForm>();
  const assetClassId = row.id;

  // Hide liquidity status input for the Liability and Receivable asset classes
  if (row.isLiabilityClass || row.isReceivableClass) {
    return <CellContainer align={colDef.align} />;
  }

  return (
    <CellContainer align={colDef.align}>
      <Box width="100%" pl={1.25}>
        <FormAwareRadioGroup
          fieldName={
            `assetCategoriesById.${assetClassId}.liquidityStatus` as const satisfies AssetCategoriesFormPaths
          }
          label="Liquidity status"
          hideLabel
          control={control}
          row
          options={[
            {
              label: '',
              disabled: row.isSystemClass,
              value: AssetClassLiquidityStatus.Liquid,
            },
            {
              label: '',
              disabled: row.isSystemClass,
              value: AssetClassLiquidityStatus.Illiquid,
            },
          ]}
        />
      </Box>
    </CellContainer>
  );
}

function AssetCategoryNameInputRenderer({
  row,
  colDef,
}: GridRenderCellParams<RowData>) {
  const [isDeletingAssetCategory, setIsDeletingAssetCategory] = useState(false);
  const { control, setValue } = useFormContext<AssetCategoriesForm>();
  const [name, liquidity, editingRowId] = useWatch({
    control,
    name: [
      `assetCategoriesById.${row.id}.name`,
      `assetCategoriesById.${row.id}.liquidityStatus`,
      `currentlyEditingRowId`,
    ],
  });
  const { reorderableRows, setReorderableRows } = useReorderableRows();
  const assetClassId = row.id;
  const isEditingRow = row.id === editingRowId;

  const setIsEditingRow = (isEditing: boolean) => {
    setValue('currentlyEditingRowId', isEditing ? assetClassId : null);
  };

  const handleDeleteAssetCategory = () => {
    // if the row hasn't yet been saved to the backend, we can just remove it from the table and move on
    // without worrying about any remapping or anything else
    if (startsWith(row.id, NEW_ASSET_CATEGORY_SENTINEL)) {
      setIsEditingRow(false);
      // Remove the row from reorderableRows
      setReorderableRows(reorderableRows.filter((r) => r.id !== row.id));
      return;
    }

    setIsDeletingAssetCategory(true);
  };

  return (
    <>
      {isDeletingAssetCategory && (
        <AssetCategoryDeletionModal
          onClose={() => {
            setIsDeletingAssetCategory(false);
            setIsEditingRow(false);
          }}
          deletedAssetCategoryId={row.id}
          isOpen={isDeletingAssetCategory}
        />
      )}
      <CellContainer align={colDef.align}>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          width="100%"
        >
          <Stack
            direction="row"
            spacing={1}
            width="100%"
            display={isEditingRow ? 'inherit' : 'none'}
          >
            <Box flexGrow={1}>
              <FormAwareTextInput
                // Remount when editing starts so autoFocus runs after the input becomes visible.
                key={String(isEditingRow)}
                autoFocus
                fieldName={
                  `assetCategoriesById.${assetClassId}.name` as const satisfies AssetCategoriesFormPaths
                }
                label="Asset class name"
                hideLabel
                control={control}
                required={true}
                // we need to do this because the input is inside a grid cell, and there are a number
                // of event listeners on the grid cell that we don't want to trigger when the input is focused
                // see more: https://stackoverflow.com/questions/71055614/why-is-the-space-key-being-filtered-out-by-muis-text-field-component
                onKeyDown={(e) => {
                  e.stopPropagation();
                  // persist changes if the user presses enter
                  if (e.key === 'Enter') {
                    setIsEditingRow(false);
                  }
                }}
              />
            </Box>
            <Box>
              <Stack direction="row" spacing={1} ml={2}>
                <IconButton
                  variant="primary"
                  ariaLabel="Save edits"
                  size="xs"
                  icon={CheckIcon}
                  onClick={() => {
                    if (!name || (name as string).trim() === '') {
                      handleDeleteAssetCategory();
                    } else {
                      setIsEditingRow(false);
                    }
                  }}
                />
                <IconButton
                  variant="destructive-transparent"
                  ariaLabel="Delete asset category"
                  size="xs"
                  icon={Trash01Icon}
                  onClick={handleDeleteAssetCategory}
                />
              </Stack>
            </Box>
          </Stack>
          {!isEditingRow && (
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              width="100%"
              spacing={1}
            >
              <PrimaryCellTypography>{name}</PrimaryCellTypography>
              <Stack direction="row" width={50} justifyContent="center">
                {!row.isSystemClass ? (
                  <Box className={SHOW_ON_ROW_HOVER_CLASSNAME}>
                    <IconButton
                      ariaLabel="Edit asset category"
                      icon={Edit02Icon}
                      variant="transparent"
                      size="xs"
                      onClick={() => setIsEditingRow(true)}
                    />
                  </Box>
                ) : (
                  <SystemClassIndicator liquidityStatus={liquidity} />
                )}
              </Stack>
            </Stack>
          )}
        </Stack>
      </CellContainer>
    </>
  );
}
